import {
  Box,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { format } from 'date-fns';
import { ReactNode } from 'react';
import Image from 'src/components/image';
import Scrollbar from 'src/components/scrollbar';
import { TablePaginationCustom, useTable } from 'src/components/table';
import { ROLES } from 'src/sections/@dashboard/organization/components/OrganizationDetails/OrganizationInvites/OrganizationInvitesAddModal';
import { mapInvitationStatusEnumToString } from 'src/utils/mapInvitationStatusEnumToString';
import { InvitationSummaryDto } from 'woofwoof-api';
import OrganizationInvitesResendModal from './OrganizationInvitesResendModal';
import { translateKey } from 'src/locales/translate';


interface Row {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  roles: InvitationSummaryDto['roles'];
  status: InvitationSummaryDto['status'];
  updatedAt: string;
  actions: string;
}

interface Column {
  id: keyof Row;
  label: string;
  minWidth?: number;
  align?: 'left' | 'center';
  render?: (row: Row) => ReactNode;
}

interface OrganizationInvitesListTableProps {
  invites: InvitationSummaryDto[];
  isError: boolean;
  isLoading: boolean;
  fetchInvites: () => void;
}

export default function OrganizationInvitesListTable({
  invites,
  isError,
  isLoading,
  fetchInvites,
}: OrganizationInvitesListTableProps) {
  const theme = useTheme();

  const tableData: Row[] =
    //TODO: Add proper type
    invites.map((invite) => ({
      id: invite.id,
      firstName: invite.firstName,
      lastName: invite.lastName,
      email: invite.email,
      status: invite.status,
      roles: invite.roles,
      updatedAt: invite.updatedAt,
      actions: '',
    })) || [];

  const COLUMNS: Column[] = [
    {
      id: 'firstName',
      label: translateKey("hardcoded.imie_i_nazwisko"),
      render: (value: Row) => (
        <Box
          sx={{
            gap: theme.spacing(2),
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Typography variant="body2">
            {value.firstName} {value.lastName}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'email',
      label: 'E-mail',
      render: (value: Row) => value.email,
    },
    {
      id: 'roles',
      label: 'Role',
      render: (value: Row) =>
        value.roles
          .map((role) => ROLES.find((r) => r.value === role)?.label)
          .join(', '),
    },
    {
      id: 'updatedAt',
      label: translateKey("hardcoded.wyslano"),
      render: (value: Row) =>
        format(new Date(value.updatedAt), 'dd/MM/yyyy H:mm'),
    },
    {
      id: 'status',
      label: translateKey("visit.tableStatus"),
      render: (value: Row) => mapInvitationStatusEnumToString(value.status),
    },
    {
      id: 'actions',
      label: translateKey("visit.historyTableActions"),
      align: 'center',
      render: (value: Row) =>
        value.status !== 'ACCEPTED' && (
          <OrganizationInvitesResendModal
            fetchInvites={fetchInvites}
            invitationId={value.id}
          />
        ),
    },
  ];
  const { page, rowsPerPage, onChangePage, onChangeRowsPerPage } = useTable({
    defaultRowsPerPage: 25,
  });

  const TableHeader = (
    <Table stickyHeader>
      <TableHead>
        <TableRow>
          {COLUMNS.map((column) => (
            <TableCell
              key={column.id}
              align={column.align}
              style={{ minWidth: column.minWidth }}
            >
              {column.label}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
    </Table>
  );

  const FullTable = (
    <Box sx={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 }}>
      <Scrollbar>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {COLUMNS.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  style={{ minWidth: column.minWidth }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {tableData
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row, rowIndex) => (
                <TableRow key={rowIndex}>
                  {COLUMNS.map((column, index) => {
                    const value = row[column.id];
                    return (
                      <TableCell key={column.id} align={column.align}>
                        {column.render ? column.render(row) : value}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </Scrollbar>
    </Box>
  );

  return (
    <>
      <TableContainer
        sx={{ height: 1, display: 'flex', flexDirection: 'column' }}
      >
        {!isLoading && tableData.length > 0 ? (
          FullTable
        ) : (
          <>
            {TableHeader}
            <Stack
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              flex="1"
              gap={2}
            >
              {isLoading ? (
                <CircularProgress size={48} />
              ) : (
                <>
                  <Image
                    sx={{ width: '200px' }}
                    alt="empty waiting room"
                    src="/assets/images/empty_table.svg"
                  />
                  <Typography>
                    {isError
                      ? translateKey("hardcoded.nie_moglismy_pobrac_zaproszen")
                      : translateKey("hardcoded.brak_zaproszen")}
                  </Typography>
                </>
              )}
            </Stack>
          </>
        )}
      </TableContainer>

      <TablePaginationCustom
        count={tableData.length}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={onChangePage}
        onRowsPerPageChange={onChangeRowsPerPage}
      />
    </>
  );
}
